import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  CheckCircle,
  Award,
  FileText,
  Bell,
  BookOpen,
  ClipboardList,
  Loader2,
  AlertCircle,
  Clock,
  GraduationCap,
  Building2,
  RefreshCw,
} from 'lucide-react';
import { useClassDetail, useHasStudentsWithRattrapage } from '../hooks/useProfessor';
import { fetchAcademicYears } from '@/services/academicPlanningService';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { getDayName } from '@/types/professor.types';

// Tab Components
import StudentsTab from '../components/class-detail/StudentsTab';
import AttendanceTab from '../components/class-detail/AttendanceTab';
import GradesTab from '../components/class-detail/GradesTab';
import SyllabusTab from '../components/class-detail/SyllabusTab';
import AnnouncementsTab from '../components/class-detail/AnnouncementsTab';
import ContentTab from '../components/class-detail/ContentTab';
import AssignmentsTab from '../components/class-detail/AssignmentsTab';
import RattrapagesTab from '../components/class-detail/RattrapagesTab';
type TabId = 'students' | 'attendance' | 'grades' | 'syllabus' | 'content' | 'assignments' | 'announcements' | 'rattrapages';

interface NavItem {
  id: TabId;
  label: string;
  icon: React.ElementType;
  badge?: number | string;
  badgeColor?: string;
}

const ClassDetailPage: React.FC = () => {
  const { groupeId, moduleElementId } = useParams<{ groupeId: string; moduleElementId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const academicYearId = searchParams.get('academicYearId') || undefined;
  const { tokens } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Get active tab from URL or default to 'announcements'
  const tabFromUrl = searchParams.get('tab') as TabId | null;
  const validTabs: TabId[] = ['announcements', 'students', 'attendance', 'syllabus', 'content', 'assignments', 'grades', 'rattrapages'];
  const activeTab: TabId = tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : 'announcements';

  // Update tab in URL when changed
  const setActiveTab = (tab: TabId) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams, { replace: true });
  };

  // Fetch class detail
  const { data: classDetail, isLoading, error, refetch } = useClassDetail(
    groupeId || null,
    moduleElementId || null,
    academicYearId
  );

  // Check if there are students with rattrapage
  const { data: rattrapageStatus } = useHasStudentsWithRattrapage(
    groupeId || null,
    moduleElementId || null,
    academicYearId
  );

  // A year other than the live one is consultation only: the API refuses every
  // write outside the current year, so the tabs must not offer them either.
  const [currentYearId, setCurrentYearId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!tokens?.accessToken) return;
    fetchAcademicYears(tokens.accessToken)
      .then((years) => setCurrentYearId(years.find((y) => y.isCurrent)?.id))
      .catch(() => setCurrentYearId(undefined));
  }, [tokens?.accessToken]);
  const isPastYear = !!academicYearId && !!currentYearId && academicYearId !== currentYearId;

  // Build navigation items
  const baseNavItems: NavItem[] = [
    {
      id: 'announcements',
      label: 'Annonces',
      icon: Bell,
      badge: classDetail?.contentStats.announcementsCount || undefined,
    },
    {
      id: 'students',
      label: 'Étudiants',
      icon: Users,
      badge: classDetail?.studentCount,
    },
    {
      id: 'attendance',
      label: 'Présences',
      icon: CheckCircle,
    },
    {
      id: 'syllabus',
      label: 'Syllabus',
      icon: FileText,
      badge: !classDetail?.contentStats.syllabusUploaded ? '!' : undefined,
      badgeColor: 'bg-amber-100 text-amber-700',
    },
    {
      id: 'content',
      label: 'Contenu',
      icon: BookOpen,
      badge: classDetail?.contentStats.materialsCount || undefined,
    },
    {
      id: 'assignments',
      label: 'Devoirs',
      icon: ClipboardList,
      badge: classDetail?.contentStats.assignmentsCount || undefined,
    },
    {
      id: 'grades',
      label: 'Notes',
      icon: Award,
      badge: !classDetail?.gradeStatus.configured ? '!' : undefined,
      badgeColor: 'bg-amber-100 text-amber-700',
    },
  ];

  // Add Rattrapages tab if there are students with rattrapage
  const navItems: NavItem[] = rattrapageStatus?.hasRattrapages
    ? [
        ...baseNavItems,
        {
          id: 'rattrapages',
          label: 'Rattrapages',
          icon: RefreshCw,
          badgeColor: 'bg-orange-100 text-orange-700',
        },
      ]
    : baseNavItems;

  // Handle back navigation — keep the current role segment (page is shared by
  // teachers and staff who also teach: /teacher, /filiere, /dp, …)
  const handleBack = () => {
    navigate(`${location.pathname.split('/mes-cours')[0]}/mes-cours`);
  };

  // Render tab content
  const renderTabContent = () => {
    if (!groupeId || !moduleElementId) return null;

    switch (activeTab) {
      case 'students':
        return <StudentsTab groupeId={groupeId} moduleElementId={moduleElementId} academicYearId={academicYearId} />;
      case 'attendance':
        return (
          <AttendanceTab
            groupeId={groupeId}
            moduleElementId={moduleElementId}
            academicYearId={academicYearId}
          />
        );
      case 'grades':
        return (
          <GradesTab
            groupeId={groupeId}
            moduleElementId={moduleElementId}
            academicYearId={academicYearId}
            readOnly={isPastYear}
          />
        );
      case 'syllabus':
        return <SyllabusTab groupeId={groupeId} moduleElementId={moduleElementId} academicYearId={academicYearId} />;
      case 'content':
        return <ContentTab groupeId={groupeId} moduleElementId={moduleElementId} academicYearId={academicYearId} />;
      case 'assignments':
        return <AssignmentsTab groupeId={groupeId} moduleElementId={moduleElementId} academicYearId={academicYearId} />;
      case 'announcements':
        return <AnnouncementsTab groupeId={groupeId} moduleElementId={moduleElementId} academicYearId={academicYearId} />;
      case 'rattrapages':
        return (
          <RattrapagesTab
            groupeId={groupeId}
            moduleElementId={moduleElementId}
            academicYearId={academicYearId}
            readOnly={isPastYear}
          />
        );
      default:
        return null;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Error state
  if (error || !classDetail) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft size={20} />
            <span>Retour à mes cours</span>
          </button>
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg flex items-center gap-3">
            <AlertCircle className="h-6 w-6" />
            <div>
              <p className="font-medium">Erreur lors du chargement</p>
              <p className="text-sm">{(error as Error)?.message || 'Classe non trouvée'}</p>
            </div>
            <button
              onClick={() => refetch()}
              className="ml-auto px-4 py-2 bg-red-100 hover:bg-red-200 rounded-lg transition-colors"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Compact Header */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          {/* Back button */}
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-900 text-sm mb-3 transition-colors"
          >
            <ArrowLeft size={18} />
            <span>Retour à mes cours</span>
          </button>

          {/* Title and badges */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {classDetail.moduleElementName}
              </h1>
              <p className="text-sm text-gray-500">
                {classDetail.moduleCode} - {classDetail.moduleName}
              </p>
            </div>

            {/* Badges and schedule - compact */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                <GraduationCap className="h-3.5 w-3.5" />
                {classDetail.groupeName}
              </span>
              <span className="inline-flex items-center px-2.5 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-full">
                {classDetail.filiereName} - {classDetail.niveau === 1 ? '1ère' : `${classDetail.niveau}ème`} Année
              </span>
              {classDetail.campusName && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                  <Building2 className="h-3 w-3" />
                  {classDetail.campusName}
                </span>
              )}
              {classDetail.scheduleInfo.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                  <Clock className="h-3 w-3" />
                  {classDetail.scheduleInfo.map((s, i) => (
                    <span key={i}>
                      {i > 0 && ', '}
                      {getDayName(s.dayOfWeek)} {s.startTime}-{s.endTime}
                      {s.roomName && ` (${s.roomName})`}
                    </span>
                  ))}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content with sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Vertical Sidebar Navigation */}
        <nav className="w-56 bg-white border-r border-gray-200 flex-shrink-0 overflow-y-auto">
          <div className="py-4">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors
                    ${isActive
                      ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                >
                  <Icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.badge !== undefined && (
                    <span className={`px-2 py-0.5 text-xs rounded-full ${item.badgeColor || 'bg-gray-100 text-gray-600'}`}>
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-5xl">
            {renderTabContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ClassDetailPage;
